<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { Modal, message } from "ant-design-vue";
import { AdminApiError, deleteAdminOrder, listAdminOrders, refundAdminOrder, type AdminPaymentOrder } from "../admin-api";

const orders = ref<AdminPaymentOrder[]>([]);
const loading = ref(false);
const selectedOrder = ref<AdminPaymentOrder | null>(null);
const refundTarget = ref<AdminPaymentOrder | null>(null);
const refundReason = ref("");
const refundingOrderId = ref("");
const deletingOrderId = ref("");
const searchKeyword = ref("");
const statusFilter = ref<string | undefined>(undefined);

const columns = [
  { title: "订单", key: "orderId", dataIndex: "orderId" },
  { title: "用户", key: "userId", dataIndex: "userId" },
  { title: "商品", key: "title", dataIndex: "title" },
  { title: "额度", key: "credits", dataIndex: "credits", width: 90 },
  { title: "金额", key: "amount", width: 120 },
  { title: "状态", key: "status", width: 100 },
  { title: "创建时间", key: "createdAt", width: 180 },
  { title: "操作", key: "actions", width: 210 }
];

const paidOrders = computed(() => orders.value.filter((order) => order.status.toLowerCase() === "paid"));
const paidCount = computed(() => paidOrders.value.length);
const totalCnyAmount = computed(() => sumPaidAmountByCurrency("CNY"));
const totalAudAmount = computed(() => sumPaidAmountByCurrency("AUD"));
const totalCredits = computed(() => paidOrders.value.reduce((sum, order) => sum + order.credits, 0));
const statusOptions = [
  { label: "待支付", value: "pending" },
  { label: "成功", value: "paid" },
  { label: "失败", value: "failed" },
  { label: "已关闭", value: "closed" },
  { label: "已退款", value: "refunded" }
];
const filteredOrders = computed(() => {
  const keyword = searchKeyword.value.trim().toLowerCase();
  const status = (statusFilter.value ?? "").trim().toLowerCase();
  return orders.value.filter((order) => {
    const matchesKeyword =
      !keyword ||
      order.orderId.toLowerCase().includes(keyword) ||
      (order.userNickname || "").toLowerCase().includes(keyword);
    const matchesStatus = !status || order.status.toLowerCase() === status;
    return matchesKeyword && matchesStatus;
  });
});

onMounted(loadOrders);

async function loadOrders() {
  loading.value = true;
  try {
    orders.value = await listAdminOrders();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "订单列表加载失败。");
  } finally {
    loading.value = false;
  }
}

function formatMoney(order: AdminPaymentOrder) {
  return `¥${order.amountCny.toFixed(2)} ${order.currency}`;
}

function sumPaidAmountByCurrency(currency: string) {
  return paidOrders.value
    .filter((order) => order.currency.toUpperCase() === currency)
    .reduce((sum, order) => sum + order.amountCny, 0);
}

function channelLabel(channel: string) {
  const value = channel.toLowerCase();
  if (value === "stripe") {
    return "Stripe";
  }
  if (value === "wechat_pay") {
    return "微信支付";
  }
  return channel;
}

function refundWarningMessage(order: AdminPaymentOrder) {
  return `退款会调用${channelLabel(order.channel)}，并同步回收该订单发放的额度或宠物位。`;
}

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString() : "未记录";
}

function statusColor(status: string) {
  const value = status.toLowerCase();
  if (value === "paid") {
    return "green";
  }
  if (value === "refunded") {
    return "blue";
  }
  if (value === "closed" || value === "failed") {
    return "default";
  }
  return "gold";
}

function statusLabel(status: string) {
  const value = status.toLowerCase();
  return statusOptions.find((option) => option.value === value)?.label ?? status;
}

function canRefund(order: AdminPaymentOrder) {
  return order.status.toLowerCase() === "paid";
}

function canDelete(order: AdminPaymentOrder) {
  const status = order.status.toLowerCase();
  return status !== "paid" && status !== "refunded";
}

function openRefund(order: AdminPaymentOrder) {
  refundTarget.value = order;
  refundReason.value = "后台退款";
}

function closeRefund() {
  refundTarget.value = null;
  refundReason.value = "";
}

async function submitRefund() {
  if (!refundTarget.value) {
    return;
  }

  refundingOrderId.value = refundTarget.value.orderId;
  try {
    const response = await refundAdminOrder(refundTarget.value.orderId, refundReason.value);
    message.success(response.message);
    orders.value = orders.value.map((order) => (order.orderId === response.order.orderId ? response.order : order));
    if (selectedOrder.value?.orderId === response.order.orderId) {
      selectedOrder.value = response.order;
    }
    closeRefund();
  } catch (currentError) {
    if (currentError instanceof AdminApiError && currentError.code === "refund_credit_balance_not_enough") {
      Modal.error({
        title: "暂时不能退款",
        content: currentError.message || "用户的生成额度已经被使用，剩余额度不足以回收，不能直接退款。"
      });
    } else if (currentError instanceof AdminApiError && currentError.code === "refund_pet_slot_conflict") {
      Modal.error({
        title: "暂时不能退款",
        content: currentError.message || "用户当前宠物数量超过退款后的宠物位上限，请先人工处理宠物位。"
      });
    } else {
      message.error(currentError instanceof Error ? currentError.message : "退款失败。");
    }
  } finally {
    refundingOrderId.value = "";
  }
}

async function submitDelete(order: AdminPaymentOrder) {
  deletingOrderId.value = order.orderId;
  try {
    await deleteAdminOrder(order.orderId);
    orders.value = orders.value.filter((item) => item.orderId !== order.orderId);
    if (selectedOrder.value?.orderId === order.orderId) {
      selectedOrder.value = null;
    }
    if (refundTarget.value?.orderId === order.orderId) {
      closeRefund();
    }
    message.success("订单已删除。");
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "订单删除失败。");
  } finally {
    deletingOrderId.value = "";
  }
}
</script>

<template>
  <a-space class="admin-page-stack" direction="vertical" size="large">
    <a-row :gutter="[16, 16]">
      <a-col :xs="24" :sm="12" :xl="5"><a-card><a-statistic title="订单总数" :value="orders.length" /></a-card></a-col>
      <a-col :xs="24" :sm="12" :xl="4"><a-card><a-statistic title="已支付" :value="paidCount" /></a-card></a-col>
      <a-col :xs="24" :sm="12" :xl="5"><a-card><a-statistic title="人民币订单金额" :value="totalCnyAmount" prefix="¥" :precision="2" /></a-card></a-col>
      <a-col :xs="24" :sm="12" :xl="5"><a-card><a-statistic title="澳币订单金额" :value="totalAudAmount" prefix="A$" :precision="2" /></a-card></a-col>
      <a-col :xs="24" :sm="12" :xl="5"><a-card><a-statistic title="售出额度" :value="totalCredits" /></a-card></a-col>
    </a-row>

    <a-card title="订单记录">
      <div class="admin-filter-bar">
        <a-input
          v-model:value="searchKeyword"
          class="admin-filter-bar__search"
          allow-clear
          placeholder="搜索订单号 / 用户昵称"
        />
        <a-select
          v-model:value="statusFilter"
          class="admin-filter-bar__select"
          allow-clear
          placeholder="订单状态"
          :options="statusOptions"
        />
      </div>
      <a-empty v-if="!loading && orders.length === 0" description="还没有购买额度订单" />
      <a-table
        v-else
        row-key="orderId"
        :columns="columns"
        :data-source="filteredOrders"
        :loading="loading"
        :pagination="{ pageSize: 10, showSizeChanger: true }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'orderId'">
            <strong>{{ record.orderId }}</strong>
            <div class="admin-table-subtext">{{ channelLabel(record.channel) }}</div>
          </template>
          <template v-else-if="column.key === 'userId'">
            <strong>{{ record.userNickname || "未设置昵称" }}</strong>
            <div class="admin-table-subtext">{{ record.userId }}</div>
          </template>
          <template v-else-if="column.key === 'amount'">{{ formatMoney(record) }}</template>
          <template v-else-if="column.key === 'status'"><a-tag :color="statusColor(record.status)">{{ statusLabel(record.status) }}</a-tag></template>
          <template v-else-if="column.key === 'createdAt'">{{ formatDate(record.createdAt) }}</template>
          <template v-else-if="column.key === 'actions'">
            <a-space>
              <a-button size="small" @click="selectedOrder = record">查看</a-button>
              <a-button
                v-if="canRefund(record)"
                size="small"
                danger
                :loading="refundingOrderId === record.orderId"
                @click="openRefund(record)"
              >
                退款
              </a-button>
              <a-popconfirm
                title="确定删除这笔未支付订单吗？已支付和已退款订单会保留审计记录。"
                ok-text="删除"
                cancel-text="取消"
                :disabled="!canDelete(record)"
                @confirm="submitDelete(record)"
              >
                <a-button
                  size="small"
                  danger
                  :disabled="!canDelete(record)"
                  :loading="deletingOrderId === record.orderId"
                >
                  删除
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-drawer title="订单详情" :open="Boolean(selectedOrder)" width="460" @close="selectedOrder = null">
      <a-descriptions v-if="selectedOrder" bordered :column="1" size="small">
        <a-descriptions-item label="订单号">{{ selectedOrder.orderId }}</a-descriptions-item>
        <a-descriptions-item label="用户昵称">{{ selectedOrder.userNickname || "未设置昵称" }}</a-descriptions-item>
        <a-descriptions-item label="用户 ID">{{ selectedOrder.userId }}</a-descriptions-item>
        <a-descriptions-item label="商品">{{ selectedOrder.title }} / {{ selectedOrder.productId }}</a-descriptions-item>
        <a-descriptions-item label="额度">{{ selectedOrder.credits }} 次，宠物位 {{ selectedOrder.petSlots }}</a-descriptions-item>
        <a-descriptions-item label="金额">{{ formatMoney(selectedOrder) }}</a-descriptions-item>
        <a-descriptions-item label="状态">{{ statusLabel(selectedOrder.status) }}</a-descriptions-item>
        <a-descriptions-item label="渠道">{{ channelLabel(selectedOrder.channel) }}</a-descriptions-item>
        <a-descriptions-item label="交易号">{{ selectedOrder.providerTradeNo || "未记录" }}</a-descriptions-item>
        <a-descriptions-item label="退款号">{{ selectedOrder.refundTradeNo || "未记录" }}</a-descriptions-item>
        <a-descriptions-item label="退款原因">{{ selectedOrder.refundReason || "未记录" }}</a-descriptions-item>
        <a-descriptions-item label="创建时间">{{ formatDate(selectedOrder.createdAt) }}</a-descriptions-item>
        <a-descriptions-item label="支付时间">{{ formatDate(selectedOrder.paidAt) }}</a-descriptions-item>
        <a-descriptions-item label="退款时间">{{ formatDate(selectedOrder.refundedAt) }}</a-descriptions-item>
        <a-descriptions-item label="更新时间">{{ formatDate(selectedOrder.updatedAt) }}</a-descriptions-item>
      </a-descriptions>
      <template #footer>
        <a-space v-if="selectedOrder">
          <a-button
            v-if="canRefund(selectedOrder)"
            danger
            :loading="refundingOrderId === selectedOrder.orderId"
            @click="openRefund(selectedOrder)"
          >
            退款
          </a-button>
          <a-popconfirm
            title="确定删除这笔未支付订单吗？已支付和已退款订单会保留审计记录。"
            ok-text="删除"
            cancel-text="取消"
            :disabled="!canDelete(selectedOrder)"
            @confirm="submitDelete(selectedOrder)"
          >
            <a-button
              danger
              :disabled="!canDelete(selectedOrder)"
              :loading="deletingOrderId === selectedOrder.orderId"
            >
              删除订单
            </a-button>
          </a-popconfirm>
        </a-space>
      </template>
    </a-drawer>

    <a-modal
      title="确认退款"
      :open="Boolean(refundTarget)"
      :confirm-loading="Boolean(refundingOrderId)"
      ok-text="确认退款"
      ok-type="danger"
      cancel-text="取消"
      @ok="submitRefund"
      @cancel="closeRefund"
    >
      <a-space v-if="refundTarget" direction="vertical" size="middle" style="width: 100%">
        <a-alert
          type="warning"
          show-icon
          :message="refundWarningMessage(refundTarget)"
        />
        <a-descriptions bordered :column="1" size="small">
          <a-descriptions-item label="订单号">{{ refundTarget.orderId }}</a-descriptions-item>
          <a-descriptions-item label="商品">{{ refundTarget.title }}</a-descriptions-item>
          <a-descriptions-item label="金额">{{ formatMoney(refundTarget) }}</a-descriptions-item>
        </a-descriptions>
        <a-textarea v-model:value="refundReason" :rows="3" placeholder="填写退款原因" />
      </a-space>
    </a-modal>
  </a-space>
</template>
